<template>
    <div id="integrations-account-selector">
      <auth-forms-wrapper
        show-links
        width="392px"
        card-classes="px-0"
        wrapper-classes="py-8 px-6"
      >
        <template #form>
          <v-form
            v-model="isFormValid"
            @submit.prevent="onSubmitHandler"
            id="integrations-account-selector-form"
          >
            <v-row>
              <v-col class="pb-0">
                <h3
                  v-text="'You Are Almost Done'"
                  class="secondary--font text--primary pb-2"
                />
                <p
                  v-text="'Select the account and list you want to access'"
                  class="body-2 font-regular secondary--font text--primary pb-1"
                />
  
                <account-selector
                    return-object
                    :rules="rules"
                    :module="queryString?.module"
                    clearable
                    @changed="onAccountChange"
                />
                <alert-message v-show="!!errorMessage" :message="errorMessage" />

                <list-selector
                    v-if="!isMSCNetoIntegration"
                    ref="listComponent"
                    return-object
                    :rules="listRules"
                    :disabled="selectedAccount ? false : true"
                    clearable
                    @changed="onListChange"
                />
                <alert-message v-show="!!errorMessage" :message="errorMessage" />
                
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn
                  block
                  x-large
                  type="submit"
                  color="dark-black"
                  :loading="isLoading"
                  :disabled="!isFormValid"
                  class="font-weight-bold white--text"
                  v-track="'integrations-account-selector-submit-btn'"
                >
                  DONE
                </v-btn>
  
                <v-btn
                  text
                  @click.prevent="toDashboard"
                  color="dark-black"
                  :disabled="isLoading"
                  class="mt-3 white--text"
                  v-track="'integrations-account-selector-back-to-profile-btn'"
                >
                  Back To Profile
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </template>
      </auth-forms-wrapper>

      <snackbar
        :snackbarValue="snackbar.value"
        :snackBarType="snackbar.type"
        @close="closeSnackbar"
      >
        <span class="text--primary body-2 white-space-preline">
          {{ snackbar.message }}
        </span>
      </snackbar>
    </div>
  </template>
  
  <script>
  /*eslint-disable */
  import ConnectorApp from "@/classes/ConnectorApp";
  import { notEmpty } from "@/validators/form-validators.js";
  import AccountSelector from "@/components/shared/AccountSelector.vue";
  import ListSelector from "@/components/shared/ListSelector.vue";
  
  import { mapActions, mapGetters } from "vuex";
  import { ERROR_MESSAGES } from "@/constants/app";
  import { isEmpty, queryStringToObject, sanitaiseInvalidRoute, setCluster } from "@/utils";

  import AlertMessage from "@/components/shared/AlertMessage.vue";
  import AuthFormsWrapper from "@/components/shared/AuthFormsWrapper.vue";
  import Snackbar from "@/components/shared/Snackbar.vue";
  import {createNetoConnection, createNetoIntegration, genrateToken} from "@/services/connector";

  import { genrateConnectorToken } from "@/services/users";


  import { $marketing } from "@/services/apis/http/http";
  import { $service } from "@/services/apis";
  import { SERVICE_CLUSTERS_URLS } from "@/constants/app";
  import apiConfig from "@/config/api.config";

  export default {
    name: "IntegrationsAccountSelector",
    /**
    |--------------------------------------------------
    | Components
    |--------------------------------------------------
    */
    components: { AccountSelector, AuthFormsWrapper, AlertMessage, ListSelector, Snackbar },
    /**
    |--------------------------------------------------
    | Data Properties
    |--------------------------------------------------
    */
    data() {
      return {
        isFormValid: false,
        isLoading: false,
        selectedAccount: null,
        selectedList:null,
        errorMessage: "",
        connectorConfig: new ConnectorApp(),
        rules: [notEmpty("Account Name")],
        listRules:[notEmpty("List Name")],
      };
    },
    /**
    |--------------------------------------------------
    | Computed Properties
    |--------------------------------------------------
    */
    computed: {
      ...mapGetters({
        snackbar: "ui/snackbar",
      }),
      queryString() {
        const url = new URL(window.location.href);
        let queryParams = this.connectorConfig.getRouteConfig();
  
        if (!isEmpty(queryParams)) return queryParams;
        else if (url.search?.length > 0) return queryStringToObject(url.search);
      },
      isMSCNetoIntegration() {
        return this.queryString?.module === "enable_service_eq";
      }
    },
    /**
    |--------------------------------------------------
    | Methods
    |--------------------------------------------------
    */
    methods: {
      ...mapActions({
        setHideSnackbar: "ui/setHideSnackbar",
        setSnackbar: "ui/setSnackbar",
      }),
      closeSnackbar() {
        this.setSnackbar({ value: false, message: "" });
      },
      onAccountChange(value) {
        this.setCluster(value)
        if (!this.isMSCNetoIntegration && this.$refs.listComponent) {
          this.$refs.listComponent.reloadList(value)
        }
        this.selectedAccount = value;

      },
      onListChange(value){
        this.selectedList = value;
      },
      setErrorMessage(error) {
        this.errorMessage = error;
      },
      showApiError(data) {
        const message = data?.errors || data?.error;
        if (!message) return false;

        this.setHideSnackbar(false);
        this.setSnackbar({
          value: true,
          message,
          type: this.$appConfig.snackbar.snackbarTypes.error,
        });
        return true;
      },
      async toDashboard() {
        this.$router.replace("/dashboard");
      },
      /**
       * Handles form submittion
       */
      async onSubmitHandler() {
        this.setHideSnackbar(true);
        this.setErrorMessage("");
        this.isLoading = true;
    
        const params = {}
        if (this.isMSCNetoIntegration && this.selectedAccount?.id) {
          params.account_id = this.selectedAccount.id
        }
        if (!this.isMSCNetoIntegration && this.selectedList?.id) {
          params.list_id = this.selectedList.id
        }
        params[`${this.queryString.integration_type}_domain`] = this.queryString.store_domain

        const request = this.isMSCNetoIntegration
          ? createNetoIntegration(params)
          : createNetoConnection(this.queryString.integration_type, params, {
            headers: {"http-auth": await this.generateToken()},
          });
        request
          .then(res => {
            if (this.showApiError(res.data)) return;
            window.location.href = res.data.redirect_url
          })
          .catch((err) => {
            this.showApiError(err.response?.data);
          })
          .finally(() => {
            this.isLoading = false;
            this.setHideSnackbar(false);
          })
      },
      async generateToken(){
            try{
                let { data } = await genrateConnectorToken(this.selectedAccount.id);
                return data.access_token;
            }
            catch(err){
                console.log("error is", err)
                this.isLoading = false;
                this.setHideSnackbar(false);
            }
      },
      async setCluster(account) {
            let cluster = ""
            if(account.cluster == "canada"){
                cluster = "api-ca1"
            }
            else if(account.cluster == "europe"){
                cluster = "api-eu1"
            }
            else if(account.cluster == "australia"){
                cluster = "api-au1"
            }
            else 
                cluster = `${account.cluster}-web`
            $marketing.defaults.baseURL = `https://${cluster}.maropost.com/v2/`
            $service.defaults.baseURL = SERVICE_CLUSTERS_URLS[account.cluster] || apiConfig.SERVICE_APP_BASE_URL
        }
    },
   
  };
  </script>
  